import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n/index.jsx';
import { pickLang } from '../../content.jsx';
import RichText from '../../components/RichText.jsx';
import { textOf, typoProps } from '../../richText.js';
import { arrangeTeam, levelLook, openEmail, displayPhoneIntl, telHref, whatsappHref, initials, flagUrl } from '../../team.js';
import './teamLevels.css';

// Team people by level: Direction (large feature cards) → Senior management
// (medium) → Management / Team (compact). Rows lay themselves out by the
// number of people (levelLook) unless the level has a manual layout. The
// layout follows the width of this block (container queries), so the
// admin preview at desktop / tablet / phone matches the site.

const ICONS = {
  mail: 'M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zM22 6l-10 7L2 6',
  phone: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z',
  whatsapp: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
  linkedin: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 6a2 2 0 100-4 2 2 0 000 4z',
};
const Icon = ({ d }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d={d} /></svg>
);

// Admin preview cards carry the raw email / phone and their toggles;
// public ones carry an encoded email and only the public phone.
function contactOf(m, preview) {
  return {
    email: preview ? !!(m.email && m.showEmail) : !!m.emailEnc,
    phone: preview ? (m.showPhone ? m.phone : '') : m.phone,
  };
}

export function Contacts({ m, preview }) {
  const { t } = useI18n();
  const c = contactOf(m, preview);
  if (!c.email && !c.phone && !m.whatsapp && !m.linkedin) return null;
  const stop = preview ? (e) => e.preventDefault() : undefined;
  return (
    <div className="tl-contacts">
      {c.email && <a href="#contact" className="tl-contact" onClick={(e) => { e.preventDefault(); if (!preview) openEmail(m.emailEnc); }} aria-label={t('team.emailTo', { name: m.name })} title={t('team.email')}><Icon d={ICONS.mail} /></a>}
      {c.phone && <a href={telHref(c.phone)} onClick={stop} className="tl-contact" aria-label={`${t('team.phone')} ${displayPhoneIntl(c.phone)}`} title={displayPhoneIntl(c.phone)}><Icon d={ICONS.phone} /></a>}
      {m.whatsapp && <a href={whatsappHref(m.whatsapp)} onClick={stop} target="_blank" rel="noopener noreferrer" className="tl-contact is-whatsapp" aria-label={`WhatsApp — ${m.name}`} title="WhatsApp"><Icon d={ICONS.whatsapp} /></a>}
      {m.linkedin && <a href={m.linkedin} onClick={stop} target="_blank" rel="noopener noreferrer" className="tl-contact is-linkedin" aria-label={`LinkedIn — ${m.name}`} title="LinkedIn"><Icon d={ICONS.linkedin} /></a>}
    </div>
  );
}

function Photo({ m, shape }) {
  const style = { objectPosition: `${m.focalX ?? 50}% ${m.focalY ?? 50}%` };
  return (
    <div className={`tl-photo is-${shape}`}>
      {m.photo ? <img src={m.photo} alt="" loading="lazy" decoding="async" style={style} /> : <span className="tl-initials" aria-hidden="true">{initials(m.name)}</span>}
    </div>
  );
}

// "Lire plus": the full introduction, bio and quote in a dialog.
function BioDialog({ m, lang, onClose, preview, typo }) {
  const { t } = useI18n();
  const closeRef = useRef(null);
  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; prev?.focus?.(); };
  }, [onClose]);
  const intro = pickLang(m.intro, lang);
  const bio = pickLang(m.bio, lang);
  const quote = pickLang(m.quote, lang);
  const tp = typoProps(typo);
  // On <body>: cards (hover lift) and the admin preview (scaled) are transformed.
  return createPortal(
    <div className={`tl-dialog-backdrop ${tp.className}`} style={tp.style} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tl-dialog" role="dialog" aria-modal="true" aria-labelledby={`tl-d-${m.id}`}>
        <button ref={closeRef} type="button" className="tl-dialog-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        <div className="tl-dialog-head">
          <Photo m={m} shape="portrait" />
          <div>
            <h3 id={`tl-d-${m.id}`}>{m.name}</h3>
            {pickLang(m.title, lang) && <p className="tl-title">{pickLang(m.title, lang)}</p>}
            {m.office && <p className="muted small">{pickLang(m.office.name, lang)}</p>}
            <Contacts m={m} preview={preview} />
          </div>
        </div>
        {intro && <p className="tl-dialog-intro">{intro}</p>}
        {bio && textOf(bio) !== intro && <RichText className="tl-dialog-bio" value={bio} />}
        {quote && <blockquote className="tl-quote">{quote}</blockquote>}
        {m.languages?.length > 0 && <ul className="tl-langs">{m.languages.map((l) => <li key={l}>{l}</li>)}</ul>}
      </div>
    </div>,
    document.body,
  );
}

export function PersonCard({ m, look, lang, order = 'photo-intro', preview, index = 0, showOffice = false, typo = null }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const bio = textOf(pickLang(m.bio, lang)); // plain text of the formatted bio
  const intro = pickLang(m.intro, lang) || bio;
  const quote = pickLang(m.quote, lang);
  const title = pickLang(m.title, lang);
  const showIntro = look.showIntro && intro;
  const more = !!(bio && bio !== intro) || (!!intro && (!look.showIntro || intro.length > (look.size === 'large' ? 320 : 160))) || (!!quote && look.size !== 'large');
  const introEl = showIntro && <p className="tl-intro">{intro}</p>;
  const nameEl = (
    <div className="tl-id">
      <h3 className="tl-name">{m.name}</h3>
      {title && <p className="tl-title">{title}</p>}
      {showOffice && m.office && (
        <p className="tl-office-tag">{flagUrl(m.office.country) && <img src={flagUrl(m.office.country)} alt="" width="16" height="12" />}{pickLang(m.office.name, lang)}</p>
      )}
    </div>
  );
  return (
    <article className={`tl-card is-${look.size}${look.wide ? ' is-wide' : ''}`} style={{ '--i': index }}>
      <Photo m={m} shape={look.photoShape} />
      <div className="tl-body">
        {order === 'photo-name' ? <>{nameEl}{introEl}</> : <>{introEl}{nameEl}</>}
        {look.size === 'large' && quote && <blockquote className="tl-quote">{quote}</blockquote>}
        {look.size !== 'large' && m.languages?.length > 0 && <ul className="tl-langs">{m.languages.map((l) => <li key={l}>{l}</li>)}</ul>}
        <Contacts m={m} preview={preview} />
        {more && <button type="button" className="tl-more" onClick={() => setOpen(true)}>{t('team.readMore')}</button>}
      </div>
      {!showIntro && intro && <p className="tl-hover" aria-hidden="true">{intro.length > 260 ? `${intro.slice(0, 257)}…` : intro}</p>}
      {open && <BioDialog m={m} lang={lang} preview={preview} typo={typo} onClose={() => setOpen(false)} />}
    </article>
  );
}

// One level's row of people, revealed (fade / rise) when scrolled into view.
function LevelRow({ group, lang, order, preview, heading, showOffice, typo }) {
  const look = levelLook(group.level, group.people.length);
  const ref = useRef(null);
  const [shown, setShown] = useState(preview);
  useEffect(() => {
    if (preview || !ref.current) return undefined;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [preview]);
  const H = heading;
  return (
    <section className={`tl-level is-${group.level.style}`}>
      {group.level.showHeading !== false && <H className="tl-level-title">{pickLang(group.level.name, lang)}</H>}
      <div ref={ref} className={`tl-row is-${look.align}${shown ? ' is-shown' : ''}`} style={{ '--cd': look.desktop, '--ct': look.tablet, '--cp': look.phone }}>
        {group.people.map((m, i) => <PersonCard key={m.id} m={m} look={look} lang={lang} order={order} preview={preview} index={i} showOffice={showOffice} typo={typo} />)}
      </div>
    </section>
  );
}

// The whole people area of the page. `officeHeader` renders an office title.
export default function TeamLevels({ doc, lang, filter = 'all', preview = false, officeHeader }) {
  const { t } = useI18n();
  const { top, sections } = arrangeTeam(doc, filter);
  const order = doc.cardOrder || 'photo-intro';
  // Where the office isn't the section, the card says which office.
  const manyOffices = new Set((doc.offices || []).filter((o) => !o.hidden && (o.members || []).some((m) => !m.hidden)).map((o) => o.id)).size > 1;
  if (!top.length && !sections.length) return <div className="empty-state"><p className="muted">{t('team.empty')}</p></div>;
  return (
    <div className="team-levels" id={preview ? undefined : 'leadership'}>
      {top.length > 0 && (
        <div className="tl-top">
          {top.map((g) => <LevelRow key={`top-${g.level.id}`} group={g} lang={lang} order={order} preview={preview} heading="h2" showOffice={manyOffices} typo={doc.typo || null} />)}
        </div>
      )}
      {sections.map((s) => (
        <div key={s.office?.id || 'all'} className={s.office ? 'tl-office' : 'tl-company'}>
          {s.office && officeHeader?.(s.office)}
          {s.groups.map((g) => <LevelRow key={g.level.id} group={g} lang={lang} order={order} preview={preview} heading={s.office ? 'h3' : 'h2'} showOffice={!s.office && manyOffices} typo={doc.typo || null} />)}
        </div>
      ))}
    </div>
  );
}
